import { SET_CURRENT_TAB } from './actions/actionTypes';

import { store } from './store';

import { v4 } from 'uuid';

class EventEmitter {
  listeners = {};
  addListener = (eventName, eventHandler) => {
    this.listeners[eventName] = this.listeners[eventName] || [];
    this.listeners[eventName].push(eventHandler);

    return () => {
      this.removeListener(eventName, eventHandler);
    };
  };

  removeListener = (eventName, eventHandler) => {
    const listenerArry = this.listeners[eventName];
    if (!listenerArry?.length) return;
    const i = listenerArry.indexOf(eventHandler);
    listenerArry.splice(i, 1);
  };

  emit = (eventName, opts) => {
    const handlers = this.listeners[eventName];
    const validHandlers = [];
    // iterate through all the handlers and trigger their callback if still around.
    handlers.forEach((handler) => {
      // check if the handler is still valid
      if (handler) {
        handler(opts);
        validHandlers.push(handler);
      }
    });
    // update the listeners to only be the valid handlers
    this.listeners[eventName] = validHandlers;
  };
}

const deepLinkEvents = new EventEmitter();

const deepLinkScreens = {
  'messaging/main': 'Messages',
};

const pendingDeepLinks = {};

let _activeTabNum = 0;

let _navigate = null;
let _currentScreen = 'Home';
const mainRouteComponents = {};

let modalProvider = null;

const Navigation = {
  setNavigate(navigate) {
    _navigate = navigate;
  },
  setCurrentScreen(currentScreen) {
    currentScreen = currentScreen?.substr(1);
    if (currentScreen === _currentScreen) return;
    if (mainRouteComponents[currentScreen]) {
      if (_currentScreen) mainRouteComponents[_currentScreen]?.componentDidDisappear?.();
      mainRouteComponents[currentScreen]?.componentDidAppear?.();
    }
    _currentScreen = currentScreen;
  },
  getNewScreenURI(screen) {
    return `/${screen}`;
  },
  setModalProviderContext(modalProviderContextProvider) {
    modalProvider = modalProviderContextProvider;
  },
  showConfirmation(confirmView) {
    modalProvider?.showConfirm(confirmView);
  },

  openScreen(screenConfig, componentId, asModal) {
    if (!componentId && !asModal) {
      this.openMainScreen(screenConfig);
      return;
    }

    let { screen, title, topBar, passProps, options } = screenConfig;
    passProps = passProps || {};
    topBar = topBar || {};
    options = {
      ...options,
      topBar: {
        ...topBar,
        title: {
          ...topBar.title,
          text: title,
        },
      },
    };
    passProps = {
      screen,
      showModal: !!asModal,
      asModal: !!asModal,
      ...passProps,
      componentId: v4(),
    };

    if (asModal) {
      modalProvider?.openModal(screenConfig, options, passProps);
    } else {
      modalProvider?.push(screenConfig, options, passProps);
    }
  },
  open(screenConfig, componentId, asModal) {
    this.openScreen(screenConfig, componentId, asModal);
  },

  closeScreen({ componentId, noBacksies }) {
    if (noBacksies) {
      this.dismissAllModals();
      return;
    }
    this.dismissModal(componentId);
  },
  close(options) {
    this.closeScreen(options);
  },

  showModal(screenConfig) {
    this.openScreen(screenConfig, null, true);
  },

  dismissModal() {
    modalProvider?.pop();
  },

  dismissAllModals({ animated = true } = {}) {
    modalProvider?.closeAll({ animated });
  },

  push(screenConfig, componentId) {
    this.openScreen(screenConfig, componentId, false);
  },

  openMainScreen(screenConfig) {
    console.info('>>> openMainScreen', screenConfig);
    this.dismissAllModals({ animated: false });

    let { screen, title } = screenConfig;

    const newPath = this.getNewScreenURI(screen);

    // console.log(`push to: ${newPath}`);

    _navigate(newPath);
    // also update the title of the webpage
    document.title = `Outpatient | ${title || screen}`;
  },

  popToRoot(screenConfig, componentId) {
    // if (screenConfig) {
    //   let { screen, title, topBar, passProps } = screenConfig;
    // }
  },
  // start with a null handler
  navigationButtonPressedHandler: [],
  navigationButtonPressed(opts) {
    if (this.navigationButtonPressedHandler.length > 0) {
      this.navigationButtonPressedHandler[this.navigationButtonPressedHandler.length - 1](opts);
    } else if (opts?.buttonId === 'cancel') {
      this.dismissAllModals();
    } else {
      console.log('default navigationButtonPressed, should be overridden when needed', opts);
    }
  },

  registerForButtonEvents(compRef, props, routeName) {
    // provide mirror functionality of react-native-navigation
    // when routeName is provided this is a main tab
    // all main tabs are loaded when the app starts up so we cant assume its appearing
    // when this is called
    //
    if (routeName) {
      mainRouteComponents[routeName] = compRef;
    }
    if (!routeName || _currentScreen === routeName) {
      compRef?.componentDidAppear?.();
    }
    // Override the navigationButtonPressed
    if (compRef.navigationButtonPressed) {
      this.navigationButtonPressedHandler.push(compRef.navigationButtonPressed);
    }

    // listen for handleDeepLink on components that have mounted
    if (compRef.handleDeepLink) {
      compRef._OP_deepLinkUnsubscribe = deepLinkEvents.addListener(
        'DeepLink',
        compRef.handleDeepLink
      );

      const pendingDeepLink = pendingDeepLinks[routeName];
      if (pendingDeepLink) {
        delete pendingDeepLinks[routeName];
        compRef.handleDeepLink(pendingDeepLink);
      }
    }
  },

  unRegisterForButtonEvents(compRef) {
    this.navigationButtonPressedHandler.pop();
    if (compRef._unsubscribeFocus) {
      compRef._unsubscribeFocus();
    }
    if (compRef._unsubscribeBlur) {
      compRef._unsubscribeBlur();
    }
    compRef._OP_deepLinkUnsubscribe?.();
  },

  mergeOptions(options, componentId) {
    modalProvider?.mergeOptions(options, componentId);
  },

  setButtons(buttons, componentId) {
    this.mergeOptions({ topBar: buttons }, componentId);
  },

  setTitle(options, componentId) {
    this.mergeOptions(
      {
        topBar: {
          title: {
            text: options.title,
          },
        },
      },
      componentId
    );
  },

  setStyle(options, componentId) {
    this.mergeOptions(
      {
        topBar: {
          ...options,
        },
      },
      componentId
    );
  },

  switchToTab(screen) {
    this.openScreen({ screen });
  },

  trackActiveTabNum(tabNum) {
    _activeTabNum = tabNum;

    store.dispatch({ type: SET_CURRENT_TAB, payload: tabNum });
  },

  getActiveTabNum(tabNum) {
    return _activeTabNum;
  },

  // DEPRECATED - just make a redux state change instead, this is unnecessary
  handleDeepLink({ link, payload }) {
    const targetScreen = deepLinkScreens[link];

    // Unlike native tabs, web only mounts the active main screen. Navigate first
    // and replay the deep link when the target screen registers its listener.
    if (targetScreen && _currentScreen !== targetScreen) {
      pendingDeepLinks[targetScreen] = { link, payload };
      this.openMainScreen({ screen: targetScreen });
      return;
    }

    // fire off a named event that can be handled by anyone
    // i.e. calendar/main => is handled by the main calendar component
    deepLinkEvents.emit('DeepLink', {
      link,
      payload,
    });
  },

  openDrawer() {},
  closeDrawer() {},
  dismissInAppNotification() {},
  showInAppNotification() {},
  showLightBox() {},
  dismissOverlay() {},
  _dismissLightBox() {},
  dismissLightBox() {},
};

export default Navigation;
